import { Address, DeliveryStatus } from './delivery.interface';

export interface PaymentTransaction {
  uid: string;
  id: number;
  referenceNumber: string;
  amount: number;
  currencyCode: string;
  description: string;
  /**
   * Epoch milliseconds, not a string — verified against dev, where
   * `GET /ecommerce/opn/v1/orders/072451` returns the JSON number `1788350933532`. The old `string`
   * type was simply wrong; nothing depended on it, since every consumer either pipes it through
   * `| date` (which accepts both) or reaches it via an `$any()` cast for unrelated null-safety.
   */
  dateTimeOfPayment: number;
  transactionStatus: TransactionStatus;
  resultUrl: string;
  returnUrl: string;
  pgCheckUrl: string;
  pgTransactionStatus: string;
  pgTransactionStatusCode: string;
  pgTransactionStatusDescription: string;
  pgReferenceNumber: string;
  redirectUrl: string;
  paymentGatewayDetails: { [key: string]: string };
  checkEligible: boolean;
  orderNumber: string;
  email: string;
  date: string;
  paymentMethods: string[];
}

export interface Order {
  uid: string;
  id: number;
  orderNumber: string;
  paymentTransaction: PaymentTransaction;
  paymentTransactionId: number;
  paymentTransactionUid: string;
  amount: number;
  email: string;
  /**
   * Epoch milliseconds. Verified against dev on order 072451: `dateTimeCheckedOut` came back as the
   * JSON number 1788350933311 and `dateTimeCompleted` as 1788350991951. Contrast `orderDate` below,
   * which really is an ISO date string.
   */
  dateTimeCheckedOut: number;
  dateTimeCompleted: number;
  status: OrderStatus;
  /**
   * What the order is for, from the backend `OrderPurpose` — `TICKET_PURCHASE` by default, or
   * `WALLET_TOP_UP` for a customer self-funding a ticket wallet. A top-up order has no cart and no
   * tickets, so the confirmation page keys its copy off this rather than telling every customer to
   * go and check their cart.
   */
  purpose?: string;
  currencyCode: string;
  description: string;
  eventId: number;
  /**
   * Epoch milliseconds, and nullable — it was `null` on the order checked against dev, so the value
   * itself is unconfirmed. Typed to match its confirmed siblings `dateTimeCheckedOut` and
   * `dateTimeCompleted` rather than left inconsistent with them.
   */
  dateTimeReserved: number | null;
  paymentMethod: PaymentMethod;
  parentId: number;
  parentUid: string;
  orderDate: string;
  processingFee: number;
  cartUid: string;
  contactNumber: string;
  name: string;
  delivery?: boolean;
  deliveryFee?: number;
  deliveryAmount?: number;
  deliveryLocationUid?: string;
  deliveryName?: string;
  deliveryAddress?: Address;
  deliveryStatus?: DeliveryStatus;
}


export enum PaymentMethod {
  CASH = 'CASH',
  FREE = 'FREE',
  OMARI = 'OMARI',
  VISA = 'VISA',
  MASTER_CARD = 'MASTER_CARD',
  ECOCASH = 'ECOCASH',
  INNBUCKS = 'INNBUCKS',
  LOCAL_BANK_CARD = 'LOCAL_BANK_CARD',
  LOCAL_BANK_TRANSFER = 'LOCAL_BANK_TRANSFER',
  ONE_MONEY = 'ONE_MONEY',
  ONLINE = 'ONLINE',
}


export enum TransactionStatus {
  FAILED = 'FAILED',
  SUCCESS = 'SUCCESS',
  PENDING = 'PENDING',
}


export enum OrderStatus {
  COMPLETED = 'COMPLETED',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED',
  RESERVED = 'RESERVED',
  IN_PROGRESS = 'IN_PROGRESS',
  INITIATED = 'INITIATED',
  /**
   * Was missing here while the backend has always had it
   * (`libraries/ecommerce-client/.../OrderStatus.java`), so an order in this state matched no
   * branch on the confirmation page and rendered with no heading and no explanation at all.
   * Reproduced on dev: cancelling a wallet top-up at the gateway leaves order 072452 as `FAILED`.
   */
  FAILED = 'FAILED',
}



export interface initiatePaymentReq{
  "returnUrl": string,
  "paymentMethod": PaymentMethod | string
}